import * as v from 'valibot';

import { logValidationErrorsToSentry } from '#shared';

import { activeSigmetSchema } from '#entities/Weather';
import type { ActiveSigmet } from '#entities/Weather';

import { EValidationIssueSeverity, hasErrorIssue } from '#shared/api';
import type { DataWithValidation, ValidationIssue } from '#shared/api';

import { unreplayableReason } from '../../lib';

export interface MapSnapshotCaseEvent {
  type: string;
  eventId: string;
  observedAt: string;
  observedAtMs: number;
  occurredAt: string;
  payload: unknown;
}

export interface MapSnapshotCaseBuffer {
  caseId: string;
  caseLabel: string | null;
  events: MapSnapshotCaseEvent[];
}

export interface MapSnapshotAircraftFix {
  aircraftId: number;
  locationTimestamp: string;
  latitude: number;
  longitude: number;
  heading: number;
}

export interface WeatherKeyframe {
  keyframeAt: string;
  sigmets: ActiveSigmet[];
}

export interface MapSnapshot {
  asOf: string;
  window: { from: string; to: string };
  cases: { buffers: MapSnapshotCaseBuffer[] };
  aircraft: MapSnapshotAircraftFix[];
  /** `null` is "no keyframe at or before `asOf`", which is not an empty sky. */
  weather: WeatherKeyframe | null;
}

export type MapSnapshotValidation =
  | { ok: true; value: DataWithValidation<MapSnapshot> }
  | { ok: false; message: string };

export const isoTimestamp = (field: string) =>
  v.pipe(
    v.string(),
    v.isoTimestamp(`@ERROR: ${field} is not a valid ISO timestamp`),
  );

// duplicate-ok: shape-only pass over MapSnapshot; items are graded separately
const snapshotShapeSchema = v.object({
  asOf: isoTimestamp('asOf'),
  window: v.object({ from: v.string(), to: v.string() }),
  cases: v.object({ buffers: v.array(v.unknown()) }),
  aircraft: v.array(v.unknown()),
  weather: v.nullable(v.unknown()),
});

export const keyframeShapeSchema = v.object({
  keyframeAt: isoTimestamp('keyframeAt'),
  sigmets: v.union([v.array(v.unknown()), v.record(v.string(), v.unknown())]),
});

const caseEventSchema = v.pipe(
  v.object({
    type: v.string(),
    eventId: v.string(),
    observedAt: isoTimestamp('observedAt'),
    occurredAt: v.string(),
    payload: v.unknown(),
  }),
  v.transform(event => ({
    ...event,
    observedAtMs: Date.parse(event.observedAt),
  })),
);

// duplicate-ok: the interface is the public type; this schema adds the caseLabel default
export const caseBufferSchema = v.object({
  caseId: v.string(),
  caseLabel: v.optional(v.nullable(v.string()), null),
  events: v.array(caseEventSchema),
});

// duplicate-ok: the interface is the public type; this schema checks each fix on its own
export const aircraftFixSchema = v.object({
  aircraftId: v.number(),
  locationTimestamp: isoTimestamp('locationTimestamp'),
  latitude: v.number(),
  longitude: v.number(),
  heading: v.number(),
});

export const toIssue = (
  path: (string | number)[],
  message: string,
  severity: EValidationIssueSeverity,
): ValidationIssue => {
  const pathKey = path.join('.');
  return { path, pathKey, severity, message, key: pathKey };
};

/**
 * Parses every item on its own, so one bad item costs only itself. An item
 * whose only issues are `@WARNING:` is kept, as REST keeps it.
 */
export const gradeItems = <T>(
  collection: string,
  schema: v.GenericSchema<unknown, T>,
  items: readonly unknown[],
  issues: ValidationIssue[],
): T[] => {
  const kept: T[] = [];
  items.forEach((item, index) => {
    const result = v.safeParse(schema, item);
    if (result.success) {
      kept.push(result.output);
      return;
    }
    const summary = v.summarize(result.issues);
    if (hasErrorIssue(result.issues)) {
      issues.push(
        toIssue(
          [collection, index],
          `Malformed ${collection} item dropped: ${summary}`,
          EValidationIssueSeverity.ERROR,
        ),
      );
      return;
    }
    // Sound while no schema graded here transforms after a `@WARNING:` check:
    // every structural check passed, so the output has the shape of `T`.
    kept.push(result.output as T);
    issues.push(
      toIssue(
        [collection, index],
        `${collection} item kept with a warning: ${summary}`,
        EValidationIssueSeverity.WARNING,
      ),
    );
  });
  return kept;
};

const failSnapshot = (message: string): MapSnapshotValidation => {
  const issue: ValidationIssue = {
    path: ['root'],
    pathKey: 'root',
    severity: EValidationIssueSeverity.ERROR,
    message,
    key: 'root',
  };
  logValidationErrorsToSentry('mapSnapshot', [issue], 1, 1);
  return { ok: false, message };
};

export const validateMapSnapshotResponse = (
  response: unknown,
): MapSnapshotValidation => {
  const shape = v.safeParse(snapshotShapeSchema, response);
  if (!shape.success) {
    return failSnapshot(
      `Map snapshot has an unusable shape: ${v.summarize(shape.issues)}`,
    );
  }
  const { asOf, window, cases, aircraft, weather } = shape.output;

  const issues: ValidationIssue[] = [];
  let rawSigmets: unknown[] = [];
  let keyframeAt: string | null = null;
  if (weather !== null) {
    const keyframe = v.safeParse(keyframeShapeSchema, weather);
    if (keyframe.success) {
      keyframeAt = keyframe.output.keyframeAt;
      rawSigmets = Array.isArray(keyframe.output.sigmets)
        ? keyframe.output.sigmets
        : Object.values(keyframe.output.sigmets);
    } else {
      // Read as no keyframe, so the map says weather is missing rather than
      // drawing a clear sky or blanking cases and aircraft with it.
      issues.push(
        toIssue(
          ['weather'],
          `Weather keyframe dropped: ${v.summarize(keyframe.issues)}`,
          EValidationIssueSeverity.ERROR,
        ),
      );
    }
  }

  const buffers = gradeItems(
    'buffers',
    caseBufferSchema,
    cases.buffers,
    issues,
  );
  buffers.forEach(buffer => {
    const reason = unreplayableReason(buffer);
    if (reason !== null) {
      issues.push(
        toIssue(
          ['buffers', buffer.caseId],
          `Case buffer cannot replay: ${reason}`,
          EValidationIssueSeverity.ERROR,
        ),
      );
    }
  });
  const fixes = gradeItems('aircraft', aircraftFixSchema, aircraft, issues);
  const sigmets = gradeItems('sigmets', activeSigmetSchema, rawSigmets, issues);

  const errorIssues = issues.filter(
    issue => issue.severity === EValidationIssueSeverity.ERROR,
  );
  if (errorIssues.length > 0) {
    const total =
      cases.buffers.length +
      aircraft.length +
      rawSigmets.length +
      (weather === null ? 0 : 1);
    logValidationErrorsToSentry(
      'mapSnapshot',
      errorIssues,
      total,
      errorIssues.length,
    );
  }

  return {
    ok: true,
    value: {
      data: {
        asOf,
        window,
        cases: { buffers },
        aircraft: fixes,
        weather: keyframeAt === null ? null : { keyframeAt, sigmets },
      },
      generatedAt: null,
      validation: { isValid: issues.length === 0, issues },
    },
  };
};
